import React, { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-hot-toast";

import { apiRequest } from "@mhj/shared/api";
import { useAuth } from "@mhj/shared/auth";
import { roleSatisfies } from "@mhj/shared/roles";
import type { AdminAuditLog, CuriousQuestion, ScholarshipApplication } from "@mhj/shared/types";

type ScholarshipSummary = {
  totalApplications: number;
  submitted: number;
  underReview: number;
  approved: number;
  disbursed: number;
  rejected: number;
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const role = user?.role;

  // Mirrors the server's own route guards (via the same roleSatisfies the
  // server's requireRole is built on, so `admin`'s composite access is
  // handled once, not re-guessed here): a role that can't call
  // /admin/scholarships, /admin/curious or /admin/audit shouldn't have the
  // dashboard call them on its behalf. Doing so unconditionally used to send
  // an empowerment reviewer three 403s the moment they logged in (D12 item 1)
  // — the same "door that doesn't open" problem the nav had.
  const canSeeScholarships = !!role && roleSatisfies(["scholarship_reviewer", "super_admin"], role);
  const canSeeCurious = !!role && roleSatisfies(["content_editor", "super_admin"], role);
  const canSeeAudit = !!role && roleSatisfies(["super_admin"], role);

  const { data, error } = useQuery({
    queryKey: ["admin", "dashboard", role],
    queryFn: async () => {
      const [applications, curious, audit] = await Promise.all([
        canSeeScholarships
          ? apiRequest<{ applications: ScholarshipApplication[] }>("/admin/scholarships")
          : null,
        canSeeCurious
          ? apiRequest<{ questions: CuriousQuestion[] }>("/admin/curious")
          : null,
        canSeeAudit ? apiRequest<{ logs: AdminAuditLog[] }>("/admin/audit") : null,
      ]);

      const scholarshipSummary: ScholarshipSummary | null = applications
        ? {
            totalApplications: applications.applications.length,
            submitted: applications.applications.filter((item) => item.status === "submitted").length,
            underReview: applications.applications.filter((item) => item.status === "under_review").length,
            approved: applications.applications.filter((item) => item.status === "approved").length,
            disbursed: applications.applications.filter((item) => item.status === "disbursed").length,
            rejected: applications.applications.filter((item) => item.status === "rejected").length,
          }
        : null;

      const pendingQna = curious
        ? curious.questions.filter((item) => item.status === "pending").length
        : null;

      return {
        scholarshipSummary,
        pendingQna,
        recentLogs: audit ? audit.logs.slice(0, 6) : null,
      };
    },
  });

  const scholarshipSummary = data?.scholarshipSummary ?? null;
  const pendingQna = data?.pendingQna ?? null;
  const recentLogs = data?.recentLogs ?? null;
  const loading = !data;
  const hasNothingToShow = !loading && !canSeeScholarships && !canSeeCurious && !canSeeAudit;

  useEffect(() => {
    if (error) {
      toast.error((error as Error).message);
    }
  }, [error]);

  return (
    <div className="space-y-6">
      <div className="glass-card rounded-3xl p-6">
        <p className="text-xs uppercase tracking-[0.3em] text-blue-300">
          Admin dashboard
        </p>
        <h2 className="mt-3 text-2xl font-semibold text-black">
          Operational overview
        </h2>
        <p className="mt-2 text-sm text-black/60">
          Monitor applications, Q&amp;A moderation, and audit activity.
        </p>
        {loading ? (
          <p className="mt-6 text-sm text-black/60">Loading summary…</p>
        ) : hasNothingToShow ? (
          <p className="mt-6 text-sm text-black/60">
            Nothing to summarise here for your role yet — use the sections in the
            sidebar.
          </p>
        ) : (
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {(
              [
                scholarshipSummary && ["Total applications", scholarshipSummary.totalApplications],
                scholarshipSummary && ["Submitted", scholarshipSummary.submitted],
                scholarshipSummary && ["Under review", scholarshipSummary.underReview],
                scholarshipSummary && ["Approved", scholarshipSummary.approved],
                scholarshipSummary && ["Disbursed", scholarshipSummary.disbursed],
                scholarshipSummary && ["Rejected", scholarshipSummary.rejected],
                pendingQna !== null && ["Pending Q&A", pendingQna],
              ].filter(Boolean) as [string, number][]
            ).map(([label, value]) => (
              <div
                key={label}
                className="rounded-2xl border border-black/10 bg-white/50 p-4"
              >
                <p className="text-xs uppercase tracking-[0.2em] text-black/50">
                  {label}
                </p>
                <p className="mt-3 text-2xl font-semibold text-primary-500">
                  {value}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
      {canSeeAudit && (
        <div className="glass-card rounded-3xl p-6">
          <p className="text-xs uppercase tracking-[0.3em] text-blue-400">
            Recent admin actions
          </p>
          <div className="mt-4 space-y-3 text-sm text-black/70">
            {!recentLogs || recentLogs.length === 0 ? (
              <p>No audit logs yet.</p>
            ) : (
              recentLogs.map((log) => (
                <div
                  key={log.id}
                  className="rounded-2xl border border-black/10 bg-white/50 px-4 py-3"
                >
                  <p className="text-black">
                    {log.action} — {log.entity} {log.entityId ? `(${log.entityId})` : ""}
                  </p>
                  <p className="text-xs text-black/40">{log.createdAt}</p>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
